import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import type {ColumnDef} from "@tanstack/react-table";
import {useNavigate, useSearchParams} from "react-router-dom";
import {useCallback, useMemo} from "react";
import {useTranslation} from "react-i18next";
import type {
    AggregationCorrespondenceMessageListItem
} from "crqlar-react-common/dist/models/aggregation/aggregation.model";
import type {
    CorrespondenceMessageListRequest
} from "crqlar-react-common/dist/models/correspondence/correspondenceMessageListRequest.model";
import {
    CorrespondenceAggregationService
} from "crqlar-react-common/dist/services/aggregation/correspondenceAggregation.service";
import {useTrackScreenVisit} from "crqlar-react-common/dist/utils/analytics.util";
import FilterProfileIdInput from "crqlar-react-common/dist/components/filters/input/FilterProfileIdInput";
import {
    ServerTable,
    ServerTableColumns,
    ServerTableExport,
    ServerTableFrame,
    ServerTableFullscreen,
    ServerTableProvider,
    ServerTableSearch,
    ServerTableSort,
    ServerTableToolbar,
    serverTableExportLimit,
    type ServerTableExportQuery,
    useServerTable
} from "../../common/serverTable";
import {DateElement} from "../../common/uiElements/DateElement";
import SettingsListPage from "../layout/SettingsListPage";

const CorrespondenceMessagesList = () => {
    useTrackScreenVisit('system correspondence messages');

    const {t} = useTranslation();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    const filterProfileIds = searchParams.get('profileIds')?.split(',').filter(Boolean);
    const setFilterProfileIds = (profileIds?: string[]) => {
        const newParams = new URLSearchParams(searchParams);
        if (profileIds?.length) {
            newParams.set('profileIds', profileIds.join(','));
        } else {
            newParams.delete('profileIds');
        }
        setSearchParams(newParams, {replace: true});
    };

    const columns = useMemo<ColumnDef<AggregationCorrespondenceMessageListItem>[]>(() => [
        {
            id: 'toEmail',
            header: t('Email'),
            accessorFn: message => message.toEmail,
            cell: ({row}) => row.original.toEmail || '-',
            size: 220,
            meta: {defaultActive: true}
        },
        {
            id: 'toName',
            header: t('Name'),
            accessorFn: message => message.toName,
            cell: ({row}) => row.original.toName || '-',
            size: 200,
            meta: {defaultActive: true}
        },
        {
            id: 'subject',
            header: t('Subject'),
            accessorFn: message => message.subject,
            cell: ({row}) => (
                <Typography sx={{whiteSpace: 'pre-wrap'}}>
                    {[row.original.subject || '', row.original.simplifiedContent?.substring(0, 150) || '']
                        .filter(Boolean)
                        .join('\n')}
                </Typography>
            ),
            size: 320,
            meta: {defaultActive: true, grow: true, isServerSortable: true, serverSortField: 'subject'}
        },
        {
            id: 'channel',
            header: t('Channel'),
            accessorFn: message => message.channelType,
            cell: ({row}) => row.original.channelType ? t(`Channel ${row.original.channelType}`) : '-',
            size: 140,
            meta: {defaultActive: true, isServerSortable: true, serverSortField: 'channelType'}
        },
        {
            id: 'status',
            header: t('Status'),
            accessorFn: message => message.status,
            cell: ({row}) => row.original.status ? t(`Message status ${row.original.status}`) : '-',
            size: 140,
            meta: {defaultActive: true, isServerSortable: true, serverSortField: 'status'}
        },
        {
            id: 'sent',
            header: t('Send Time'),
            accessorFn: message => message.sent,
            cell: ({row}) => <DateElement date={row.original.sent}/>,
            size: 200,
            meta: {defaultActive: true, isServerSortable: true, serverSortField: 'timestamps.sent'}
        },
        {
            id: 'created',
            header: t('Created'),
            accessorFn: message => message.insertDate,
            cell: ({row}) => <DateElement date={row.original.insertDate}/>,
            size: 200,
            meta: {defaultActive: false, isServerSortable: true, serverSortField: 'insertDate'}
        },
        {
            id: 'id',
            header: t('ID'),
            accessorFn: message => message.id,
            cell: ({row}) => <Box>{row.original.id}</Box>,
            size: 260,
            meta: {defaultActive: false, isServerSortable: true}
        },
        {
            id: 'updateDate',
            header: t('Last updated'),
            accessorFn: message => message.updateDate,
            cell: ({row}) => <DateElement date={row.original.updateDate}/>,
            size: 200,
            meta: {defaultActive: true, isServerSortable: true}
        }
    ], [t]);

    const controller = useServerTable<AggregationCorrespondenceMessageListItem>({
        tableId: 'system-correspondence-messages',
        columns,
        getRowId: message => message.id,
        initialSorting: [{id: 'sent', desc: true}],
        queryDependencies: [filterProfileIds?.join(',')],
        queryFn: ({pagination, searchQuery, sorting}) =>
            CorrespondenceAggregationService.listMessages({
                search: searchQuery || undefined,
                profileIds: filterProfileIds,
                limit: {
                    offset: pagination.pageIndex * pagination.pageSize,
                    limit: pagination.pageSize,
                    sort: sorting ? [sorting] : undefined
                }
            })
    });

    // The export's request: the list's own, without paging.
    const exportRequest = useCallback((
        {searchQuery, sorting}: ServerTableExportQuery
    ): CorrespondenceMessageListRequest => ({
        search: searchQuery || undefined,
        profileIds: filterProfileIds,
        limit: serverTableExportLimit(sorting)
    }), [filterProfileIds]);

    return (
        <SettingsListPage title={t('Messages')} back={() => navigate('/system')}>
            <ServerTableProvider controller={controller}>
                <ServerTableFrame>
                    <ServerTableToolbar hoistOnCompact>
                        <ServerTableSearch/>
                        <ServerTableSort/>
                        <Box sx={{minWidth: 260}}>
                            <FilterProfileIdInput ids={filterProfileIds} setIds={setFilterProfileIds}/>
                        </Box>
                        <ServerTableFullscreen/>

                        <ServerTableColumns/>
                        <ServerTableExport list="correspondence" title={t('Messages')} buildRequest={exportRequest}/>
                    </ServerTableToolbar>

                    <ServerTable/>
                </ServerTableFrame>
            </ServerTableProvider>
        </SettingsListPage>
    );
};

export default CorrespondenceMessagesList;
